import React, { useEffect, useState } from 'react';
import './pixelart.css';

/* ============================================================
   PIXEL ART por código (sin GIFs) — librería compartida.
   Cada sprite es una matriz de chars + un mapa char→color.
   '.' = transparente.
   ============================================================ */
export const SPRITES = {
  rocket: {
    rows: ['...b...', '..bbb..', '..bbb..', '..bwb..', '..bbb..', '.fbbbf.', '.f.b.f.', '...o...', '..oyo..'],
    map: { b: '#e6edf3', w: '#22d3ee', f: '#e53935', o: '#ff8c1a', y: '#ffd54a' },
  },
  fire: {
    rows: ['...r...', '..rr...', '..ror..', '.rooor.', '.royor.', 'rooyoor', 'royyyor', '.rooor.'],
    map: { r: '#e53935', o: '#ff8c1a', y: '#ffd54a' },
  },
  diamond: {
    rows: ['.lwwwl.', 'lcccccl', 'wcccccw', '.ccccc.', '..ccc..', '...c...'],
    map: { l: '#a5f3fc', w: '#ffffff', c: '#22d3ee' },
  },
  // Toro — alcista (café, cuernos + orejas + hocico)
  bull: {
    rows: [
      '.h............h.',
      '.hh..........hh.',
      '..hh........hh..',
      '...bbbkkkkbbb...',
      '.bbbbbkkkkbbbbb.',
      '.bkbbbbbbbbbbkb.',
      '...bedbbbbdeb...',
      '...bbbbbbbbbb...',
      '...bmmmmmmmmb...',
      '...bmddmmddmb...',
      '...bmmmmmmmmb...',
      '....bbbbbbbb....',
    ],
    map: { h: '#e8dcc0', b: '#8a5a2e', k: '#5a3618', m: '#caa472', e: '#ffffff', d: '#241008' },
  },
  // Oso — bajista (orejas redondas con interior, hocico, ojos con pupila)
  bear: {
    rows: [
      '.rrr........rrr.',
      '.rkkr......rkkr.',
      '..rrrrrrrrrrrr..',
      '.rrrrrrrrrrrrrr.',
      'rrrrrrrrrrrrrrrr',
      'rrredrrrrrrderrr',
      'rrrrrrrrrrrrrrrr',
      'rrrrrmmmmmmrrrrr',
      'rrrrmmddddmmrrrr',
      'rrrrmmmmmmmmrrrr',
      'rrrrrmmmmmmrrrrr',
      '..rrrrrrrrrrrr..',
    ],
    map: { r: '#d83a2f', k: '#8e2018', m: '#ffcdbf', e: '#ffffff', d: '#2a0807' },
  },
  // Moneda — para el loader (gira con CSS)
  coin: {
    rows: ['..ggggg..', '.gglllgg.', 'gglldllgg', 'gglldllgg', 'gglldllgg', 'gglldllgg', 'gglldllgg', '.gglllgg.', '..ggggg..'],
    map: { g: '#f0b90b', l: '#ffd76a', d: '#a4760a' },
  },
  // Cofre — para estados vacíos
  chest: {
    rows: [
      '..wwwwwwwwww..',
      '.wwwwwwwwwwww.',
      '.wkwwwmmwwwkw.',
      '.wwwwwmmwwwww.',
      'kkkkkkkkkkkkkk',
      '.wwwwwmmwwwww.',
      '.wwwwwmlmwwww.',
      '.wwwwwmmwwwww.',
      '.wwwwwwwwwwww.',
      '.kkkkkkkkkkkk.',
    ],
    map: { w: '#b06a32', k: '#6e3e18', m: '#f0b90b', l: '#fff3c4' },
  },
  // ── Avatares (bichos) — bull/bear arriba también sirven ──
  cat: {
    rows: [
      '.l.........l.',
      'ldl.......ldl',
      '.lllllllllll.',
      'ccccccccccccc',
      'cceeccccceecc',
      'cccwwwwwwwccc',
      'cccwwwpwwwccc',
      '.dcccccccccd.',
      '..dcccccccd..',
      '...ddddddd...',
    ],
    map: { c: '#8b93a0', l: '#aeb6c0', d: '#5f6772', e: '#00d412', p: '#ff8aa0', w: '#e8edf2' },
  },
  robot: {
    rows: [
      '.....aaa.....',
      '......a......',
      '.lllllllllll.',
      'sssssssssssss',
      'shhhhhhhhhhhs',
      'shhcchhhcchhs',
      'shhhhhhhhhhhs',
      'shhccccccchhs',
      'skkkkkkkkkkks',
    ],
    map: { a: '#e53935', s: '#9aa6b2', l: '#c7d0da', k: '#6c7884', h: '#10202a', c: '#22d3ee' },
  },
  alien: {
    rows: [
      '..lllllllll..',
      '.aaaaaaaaaaa.',
      'aaaaaaaaaaaaa',
      'aaweeaaaweeaa',
      'aaeeeaaaeeeaa',
      'aaaaaaaaaaaaa',
      '.aaaaaaaaaaa.',
      '..aaaaaaaaa..',
      '...ddddddd...',
      '....ddddd....',
      '.....ddd.....',
    ],
    map: { a: '#3fd9a0', l: '#7fe9c2', d: '#26a37a', e: '#0a0e0b', w: '#ffffff' },
  },
};

// Bichos disponibles para avatares por defecto
export const AVATAR_CREATURES = ['bull', 'bear', 'cat', 'robot', 'alien'];
const AVATAR_BGS = ['#1a2418', '#15212b', '#221726', '#241c10', '#0f2620', '#241015', '#1c1c24', '#102420'];

// Avatar por defecto determinístico: bicho + fondo oscuro a partir de un seed (username)
export function creatureAvatar(seed) {
  const s = String(seed || '?');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return {
    sprite: AVATAR_CREATURES[h % AVATAR_CREATURES.length],
    bg: `radial-gradient(circle at 50% 36%, ${AVATAR_BGS[(h >>> 5) % AVATAR_BGS.length]}, #0a0e0b)`,
  };
}

/* ============================================================
   MONEDAS / MEDALLAS — disco pixel + símbolo (generado por código)
   ============================================================ */
function darken(hex, f = 0.55) {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.round(((n >> 16) & 255) * f), g = Math.round(((n >> 8) & 255) * f), b = Math.round((n & 255) * f);
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}
function lighten(hex, f = 1.35) {
  const n = parseInt(hex.slice(1), 16);
  const cl = (v) => Math.min(255, Math.round(v * f));
  const r = cl((n >> 16) & 255), g = cl((n >> 8) & 255), b = cl(n & 255);
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}
function luminance(hex) {
  const n = parseInt(hex.slice(1), 16);
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
}

// Símbolos 7×7 ('x' = pixel del símbolo)
const SYM = {
  BTC:  ['.x.x...', '.xxxx..', '.x..xx.', '.xxxx..', '.x..xx.', '.xxxx..', '.x.x...'],
  ETH:  ['...x...', '..xxx..', '.xxxxx.', 'xxxxxxx', '.xxxxx.', '..xxx..', '...x...'],
  BNB:  ['...x...', '..x.x..', '.x...x.', 'x.....x', '.x...x.', '..x.x..', '...x...'],
  SOL:  ['.......', '.xxxxx.', '.......', '.xxxxx.', '.......', '.xxxxx.', '.......'],
  LTC:  ['..x....', '..x....', '.xxx...', '..x....', '..x....', '..xxxx.', '.......'],
  TRX:  ['.xxxxx.', '...x...', '...x...', '...x...', '...x...', '...x...', '.......'],
  USDT: ['.xxxxx.', '...x...', '..xxx..', '...x...', '...x...', '...x...', '.......'],
  USDC: ['...x...', '..xxxx.', '.x.x...', '..xxx..', '...x.x.', '.xxxx..', '...x...'],
  ADA:  ['..xxx..', '.x...x.', '.x...x.', '.xxxxx.', '.x...x.', '.x...x.', '.......'],
  XRP:  ['x.....x', '.x...x.', '..x.x..', '...x...', '..x.x..', '.x...x.', 'x.....x'],
  SUI:  ['...x...', '...x...', '..xxx..', '.xxxxx.', '.xxxxx.', '.xxxxx.', '..xxx..'],
  STAR: ['...x...', '..xxx..', 'xxxxxxx', '.xxxxx.', '.xx.xx.', '.x...x.', '.......'],
};
const COIN_BRAND = { BTC: '#f7931a', ETH: '#627eea', BNB: '#f0b90b', SOL: '#9945ff', LTC: '#9aa0aa', TRX: '#ef0027', USDT: '#26a17b', USDC: '#2775ca', ADA: '#3468d1', XRP: '#8a93a0', SUI: '#4da2ff' };

// Moneda pixel "acuñada" 15×15: borde + bisel (luz/sombra) + cuerpo esférico + símbolo en relieve + brillo especular.
function buildCoin(brand, symRows, symColor) {
  const SIZE = 15, c = 7, R = 7.3, OFF = 4;
  const inside = (x, y) => (x - c) ** 2 + (y - c) ** 2 <= R * R;
  const isEdge = (x, y) => inside(x, y) && (!inside(x - 1, y) || !inside(x + 1, y) || !inside(x, y - 1) || !inside(x, y + 1));
  const symAt = (x, y) => { const sy = y - OFF, sx = x - OFF; return sy >= 0 && sy < 7 && sx >= 0 && sx < 7 && symRows[sy][sx] === 'x'; };
  const sc = symColor || (luminance(brand) > 0.62 ? '#2a1d08' : '#ffffff');
  const rows = [];
  for (let y = 0; y < SIZE; y++) {
    let row = '';
    for (let x = 0; x < SIZE; x++) {
      if (!inside(x, y)) { row += '.'; continue; }
      if (symAt(x, y)) { row += 's'; continue; }
      if (isEdge(x, y)) { row += 'e'; continue; }                                   // borde oscuro
      const bevel = isEdge(x - 1, y) || isEdge(x + 1, y) || isEdge(x, y - 1) || isEdge(x, y + 1)
                 || isEdge(x - 1, y - 1) || isEdge(x + 1, y + 1) || isEdge(x - 1, y + 1) || isEdge(x + 1, y - 1);
      if (bevel) { row += (x + y) < (SIZE - 1) ? 'h' : 'k'; continue; }              // bisel: luz / sombra
      if (symAt(x - 1, y - 1)) { row += 'm'; continue; }                            // sombra del símbolo (relieve)
      if ((x === 4 && y === 3) || (x === 5 && y === 3) || (x === 3 && y === 4) || (x === 4 && y === 4)) { row += 'w'; continue; } // brillo especular
      if (y < c - 1) { row += 'g'; }                                                // cuerpo esférico: arriba claro
      else if (y > c + 2) { row += 'd'; }                                           // abajo oscuro
      else { row += 'b'; }
    }
    rows.push(row);
  }
  return {
    rows,
    map: {
      e: darken(brand, 0.4), h: lighten(brand, 1.72), k: darken(brand, 0.62),
      g: lighten(brand, 1.2), b: brand, d: darken(brand, 0.82),
      s: sc, m: darken(brand, 0.45), w: lighten(brand, 2.35),
    },
  };
}

// Íconos de pago — objetos pixel multicolor (no siluetas planas)
const PAY_SPRITES = {
  bank: {
    rows: ['.....r.....', '....rrr....', '...rrrrr...', '..rrrrrrr..', '.wwwwwwwww.', '.wowowowow.', '.wowowowow.', '.wowowowow.', '.wwwwwwwww.', '.ooooooooo.'],
    map: { r: '#f5c542', w: '#e9eef3', o: '#56636f' },
  },
  card: {
    rows: ['.bbbbbbbbb.', '.bbbbbbbbb.', '.ooooooooo.', '.bbbbbbbbb.', '.bmmbbbbbb.', '.bmmbbbbbb.', '.bbbbbbbbb.', '...........'],
    map: { b: '#3b82f6', o: '#1e3a5f', m: '#f5c542' },
  },
  mobile: {
    rows: ['..ooooooo..', '..ogggggo..', '..ogggggo..', '..ogggggo..', '..ogggggo..', '..ogggggo..', '..ogggggo..', '..ooooooo..', '..o.....o..', '..o..h..o..', '..ooooooo..'],
    map: { o: '#2b3a44', g: '#22d3ee', h: '#7df59a' },
  },
  send: {
    rows: ['h..........', 'hhp........', 'hhhpp......', 'hhhhppp....', 'ppppppppp..', 'ppppppp....', 'ppppp......', 'ppp........', 'p..........'],
    map: { h: '#9ad4ff', p: '#4db8ff' },
  },
};
const PAY_KIND = { paypal: 'send', send: 'send', crypto: 'crypto', mobile: 'mobile', bank: 'bank', card: 'card' };

// Moneda cripto pixel
export function PixelCoin({ symbol = 'BTC', px = 2, fit, className = '' }) {
  const sym = String(symbol).toUpperCase();
  const def = buildCoin(COIN_BRAND[sym] || '#8a9bb0', SYM[sym] || SYM.USDC);
  return <PixelArt rows={def.rows} map={def.map} px={px} fit={fit} className={`px-coin ${className}`} title={sym} />;
}

// Medalla de tier pixel (disco del color del tier + estrella)
export function PixelMedal({ color = '#8a9bb0', px = 2, fit, className = '' }) {
  const def = buildCoin(color, SYM.STAR, '#fffdf0');
  return <PixelArt rows={def.rows} map={def.map} px={px} fit={fit} className={`px-medal ${className}`} />;
}

// Ícono de pago pixel por categoría (objeto multicolor)
export function PixelPay({ kind = 'card', px = 2, fit, className = '' }) {
  const k = PAY_KIND[kind] || kind;
  if (k === 'crypto') return <PixelCoin symbol="USDT" px={px} fit={fit} className={className} />;
  const def = PAY_SPRITES[k] || PAY_SPRITES.card;
  return <PixelArt rows={def.rows} map={def.map} px={px} fit={fit} className={`px-pay ${className}`} />;
}

export function PixelArt({ sprite, rows, map, px = 3, fit, className, style, title }) {
  const def = sprite ? SPRITES[sprite] : { rows, map };
  if (!def || !def.rows) return null;
  const w = def.rows[0].length, h = def.rows.length;
  if (fit) px = Math.max(1, Math.floor(fit / Math.max(w, h)));
  const cells = [];
  def.rows.forEach((row, y) => [...row].forEach((ch, x) => {
    if (def.map[ch]) cells.push(<rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill={def.map[ch]} />);
  }));
  return (
    <svg className={className} style={style} width={w * px} height={h * px} viewBox={`0 0 ${w} ${h}`} shapeRendering="crispEdges" role={title ? 'img' : undefined} aria-label={title} aria-hidden={title ? undefined : true}>
      {cells}
    </svg>
  );
}

/* Loader: moneda pixel que gira */
export function PixelLoader({ px = 4, label, className = '' }) {
  return (
    <div className={`px-loader ${className}`} role="status" aria-live="polite">
      <PixelArt sprite="coin" px={px} className="px-loader-coin" />
      {label && <span className="px-loader-label">{label}</span>}
    </div>
  );
}

/* Estado vacío con ilustración pixel */
export function PixelEmpty({ sprite = 'chest', px = 5, title, hint, action, className = '' }) {
  return (
    <div className={`px-empty ${className}`}>
      <PixelArt sprite={sprite} px={px} className="px-empty-art" title={title} />
      {title && <div className="px-empty-title">{title}</div>}
      {hint && <div className="px-empty-hint">{hint}</div>}
      {action}
    </div>
  );
}

/* Mascota de ánimo de mercado: toro (alcista) / oso (bajista) */
export function MarketMood({ mood = 'bull', px = 3, className = '' }) {
  return <PixelArt sprite={mood === 'bear' ? 'bear' : 'bull'} px={px} className={`px-mood ${className}`} title={mood === 'bear' ? 'Mercado bajista' : 'Mercado alcista'} />;
}

/* Celebración: ráfaga de monedas/chispas pixel (se remonta vía prop `trigger`) */
export function PixelBurst({ trigger }) {
  const [parts, setParts] = useState(null);
  useEffect(() => {
    if (!trigger) return;
    const items = Array.from({ length: 12 }, (_, i) => {
      const angle = (i / 12) * Math.PI * 2;
      return { id: i, dx: Math.round(Math.cos(angle) * (70 + (i % 3) * 22)), dy: Math.round(Math.sin(angle) * (70 + (i % 3) * 22)) - 30, sprite: i % 3 === 0 ? 'rocket' : 'coin', delay: (i % 4) * 40 };
    });
    setParts(items);
    const t = setTimeout(() => setParts(null), 1100);
    return () => clearTimeout(t);
  }, [trigger]);
  if (!parts) return null;
  return (
    <div className="px-burst" aria-hidden="true">
      {parts.map((p) => (
        <span key={p.id} className="px-burst-part" style={{ '--dx': `${p.dx}px`, '--dy': `${p.dy}px`, animationDelay: `${p.delay}ms` }}>
          <PixelArt sprite={p.sprite} px={3} />
        </span>
      ))}
    </div>
  );
}
